import { useState, useCallback } from 'react'
import bs58 from 'bs58'
import { getNonce, verifySignature } from '../api/auth'
import { useAuth } from './AuthContext'

export type WalletType = 'phantom' | 'solflare'

type ConnectState = 'idle' | 'connecting' | 'signing' | 'verifying' | 'error'

type WalletSession = {
  walletAddress: string
  signMessage: (msg: Uint8Array) => Promise<Uint8Array>
}

async function connectPhantom(): Promise<WalletSession> {
  const win = window as any
  // Prefer window.phantom.solana (Phantom's own namespace) over window.solana,
  // which another wallet extension may have claimed. The wallet adapter also
  // uses window.phantom.solana, so both auth and signing must use the same object.
  const phantom = win.phantom?.solana ?? win.solana
  if (!phantom?.isPhantom) throw new Error('Phantom wallet not found. Install Phantom and refresh.')
  const { publicKey } = await phantom.connect()
  return {
    walletAddress: publicKey.toBase58(),
    signMessage: async (msg) => {
      const { signature } = await phantom.signMessage(msg)
      return signature
    },
  }
}

async function connectSolflare(): Promise<WalletSession> {
  const win = window as any
  const solflare = win.solflare
  await solflare.connect()
  return {
    walletAddress: solflare.publicKey.toBase58(),
    signMessage: async (msg) => {
      const result = await solflare.signMessage(msg, 'utf8')
      // Solflare may return { signature: Uint8Array } or raw Uint8Array
      return result instanceof Uint8Array ? result : result.signature
    },
  }
}

function detectAvailable(): Record<WalletType, boolean> {
  if (typeof window === 'undefined') return { phantom: false, solflare: false }
  const win = window as any
  return {
    // Check window.phantom.solana first (canonical Phantom namespace), fall back
    // to window.solana.isPhantom for setups where no other wallet has overridden it.
    phantom:  !!(win.phantom?.solana?.isPhantom) || !!(win.solana?.isPhantom),
    solflare: !!(win.solflare?.isSolflare),
  }
}

export function useWalletAuth() {
  const { login } = useAuth()
  const [state, setState] = useState<ConnectState>('idle')
  const [error, setError] = useState<string | null>(null)

  const connect = useCallback(async (walletType: WalletType) => {
    setError(null)
    setState('connecting')
    try {
      const session = walletType === 'phantom'
        ? await connectPhantom()
        : await connectSolflare()

      setState('signing')
      const { nonce, message } = await getNonce(session.walletAddress)
      const messageBytes = new TextEncoder().encode(message)
      const signatureBytes = await session.signMessage(messageBytes)
      const signatureBase58 = bs58.encode(signatureBytes)

      setState('verifying')
      const { accessToken, refreshToken } = await verifySignature(session.walletAddress, nonce, signatureBase58)

      login(session.walletAddress, accessToken, refreshToken, walletType)
      setState('idle')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed')
      setState('error')
    }
  }, [login])

  return { connect, state, error, available: detectAvailable() }
}
